import React, { useEffect, useRef, useState } from "react";
import Layout from "../../components/Layout";

// Falls back to the production endpoint so a missing build-time env var cannot
// silently break the form. Override per environment in conf/.env.*.
const API_URL = process.env.REACT_APP_CONTACT_API_URL || "https://api.everest.org/contact";

// Public by design - the site key ships in the bundle. The secret key lives
// only in the Lambda. When unset, reCAPTCHA is skipped and the API accepts the
// submission without a token, so local dev works before the keys exist.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ACTION = "contact"; // must match RECAPTCHA_ACTION in the handler

// v2 checkbox, shown only when the API says the v3 score was too low. Gives a
// real person who scored badly a way to prove themselves, instead of a refusal.
const RECAPTCHA_V2_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY;

function loadRecaptcha() {
    if (!RECAPTCHA_SITE_KEY) return Promise.resolve();
    if (window.grecaptcha) return Promise.resolve();

    const src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener("load", resolve);
            existing.addEventListener("error", reject);
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

// v3 tokens are single-use and expire after ~2 minutes, so this has to run at
// submit time - fetching on page load would fail for anyone who takes a while
// to write their message.
function getRecaptchaToken() {
    return new Promise((resolve, reject) => {
        if (!window.grecaptcha) {
            reject(new Error("reCAPTCHA not loaded"));
            return;
        }
        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION })
                .then(resolve, reject);
        });
    });
}

const INTERESTS = ["Fiat-to-crypto", "Crypto payments", "Fiat", "Stablecoins"];
const PACKAGES = ["API", "Widget / iFrame", "White-label EverWallet"];
const INDUSTRIES = ["Merchant", "Fintech", "PSP / Acquirer", "Platform", "OTC / Treasury"];

const EMPTY_FORM = {
    name: "",
    company: "",
    email: "",
    interest: INTERESTS[0],
    package: PACKAGES[0],
    industry: INDUSTRIES[0],
    message: "",
    website: "", // honeypot - hidden from real users
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(values) {
    const errors = {};
    if (values.name.trim().length < 2) errors.name = "Please enter your name.";
    if (!EMAIL_RE.test(values.email.trim())) errors.email = "Please enter a valid email address.";
    if (values.message.trim().length < 10) errors.message = "Please add a little more detail.";
    return errors;
}

const Contact = () => {
    const [values, setValues] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | sending | success | error
    const [statusMessage, setStatusMessage] = useState("");

    // How long the form has been open. The API rejects submissions that arrive
    // implausibly fast, which filters scripted posts.
    const openedAtRef = useRef(Date.now());

    // Set when the API answers with requiresChallenge, i.e. the v3 score came
    // back too low and the user needs to tick the checkbox.
    const [challengeRequired, setChallengeRequired] = useState(false);
    const challengeRef = useRef(null);
    const challengeWidgetRef = useRef(null);
    const v2TokenRef = useRef("");

    // Load the script early so submit only pays for execute(), not the download.
    useEffect(() => {
        loadRecaptcha().catch(() => {
            // Non-fatal. The submit path retries and, failing that, posts
            // without a token - the API decides whether to accept it.
            console.warn("reCAPTCHA script failed to load");
        });
    }, []);

    // Render the v2 widget once the API has asked for it. The same grecaptcha
    // object serves both versions, so no second script is needed.
    useEffect(() => {
        if (!challengeRequired || !RECAPTCHA_V2_SITE_KEY) return;
        if (!challengeRef.current || challengeWidgetRef.current !== null) return;
        if (!window.grecaptcha || !window.grecaptcha.render) return;

        challengeWidgetRef.current = window.grecaptcha.render(challengeRef.current, {
            sitekey: RECAPTCHA_V2_SITE_KEY,
            callback: (token) => {
                v2TokenRef.current = token;
            },
            "expired-callback": () => {
                v2TokenRef.current = "";
            },
            "error-callback": () => {
                v2TokenRef.current = "";
            },
        });
    }, [challengeRequired]);

    // v2 tokens are single-use, so the widget has to be cleared after every
    // attempt or a retry silently reuses a spent token.
    const resetChallenge = () => {
        v2TokenRef.current = "";
        if (challengeWidgetRef.current !== null && window.grecaptcha) {
            window.grecaptcha.reset(challengeWidgetRef.current);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (status === "sending") return;

        const nextErrors = validate(values);
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            setStatus("error");
            setStatusMessage("Please correct the highlighted fields.");
            return;
        }

        setStatus("sending");
        setStatusMessage("");
        setErrors({});

        // If the checkbox is on screen, it has to be ticked before we retry.
        if (challengeRequired && !v2TokenRef.current) {
            setStatus("error");
            setStatusMessage("Please confirm you're not a robot to send your message.");
            return;
        }

        let recaptchaToken = "";
        if (RECAPTCHA_SITE_KEY) {
            try {
                await loadRecaptcha();
                recaptchaToken = await getRecaptchaToken();
            } catch (err) {
                // Send anyway with an empty token. The API is the one that
                // decides whether a missing token is fatal, and blocking here
                // would take the form down whenever Google is unreachable.
                console.warn("reCAPTCHA unavailable:", err.message);
            }
        }

        try {
            const response = await fetch(API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...values,
                    elapsedMs: Date.now() - openedAtRef.current,
                    recaptchaToken,
                    recaptchaV2Token: v2TokenRef.current,
                }),
            });
            const data = await response.json().catch(() => ({}));

            // Score was too low: reveal the checkbox rather than turning the
            // person away. Their input is left untouched so one tick and a
            // second click sends it.
            if (data.requiresChallenge) {
                setChallengeRequired(true);
                setStatus("error");
                setStatusMessage(
                    data.error || "Please confirm you're not a robot to send your message."
                );
                return;
            }

            if (!response.ok || !data.ok) {
                if (data.fields) setErrors(data.fields);
                setStatus("error");
                setStatusMessage(
                    data.error || "Something went wrong. Please email contact@everest.org."
                );
                resetChallenge();
                return;
            }

            setStatus("success");
            setStatusMessage("Thanks — we've received your message and will be in touch shortly.");
            setValues(EMPTY_FORM);
            openedAtRef.current = Date.now();
            setChallengeRequired(false);
            resetChallenge();
        } catch (err) {
            setStatus("error");
            setStatusMessage(
                "We couldn't reach the server. Please try again or email contact@everest.org."
            );
            resetChallenge();
        }
    };

    const sending = status === "sending";

    return (
        <Layout>
            <section className="page-hero">
                <div className="container">
                    <div className="eyebrow"><span className="dot"></span> Contact</div>
                    <h1>Contact Us</h1>
                    <p>Tell us the use case, the payment flow, and the packaging you need.</p>
                </div>
            </section>

            <section className="section section-tight">
                <div className="container compare-grid">
                    <div className="form-card">
                        <form className="form-grid" onSubmit={handleSubmit} noValidate>
                            <label>
                                <span>Name</span>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Your name"
                                    value={values.name}
                                    onChange={handleChange}
                                    aria-invalid={Boolean(errors.name)}
                                    disabled={sending}
                                />
                                {errors.name && <span className="field-error">{errors.name}</span>}
                            </label>
                            <label>
                                <span>Company</span>
                                <input
                                    type="text"
                                    name="company"
                                    placeholder="Company name"
                                    value={values.company}
                                    onChange={handleChange}
                                    disabled={sending}
                                />
                            </label>
                            <label>
                                <span>Email</span>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="name@company.com"
                                    value={values.email}
                                    onChange={handleChange}
                                    aria-invalid={Boolean(errors.email)}
                                    disabled={sending}
                                />
                                {errors.email && <span className="field-error">{errors.email}</span>}
                            </label>
                            <label>
                                <span>Primary interest</span>
                                <select name="interest" value={values.interest} onChange={handleChange} disabled={sending}>
                                    {INTERESTS.map((option) => (
                                        <option key={option}>{option}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <span>Package</span>
                                <select name="package" value={values.package} onChange={handleChange} disabled={sending}>
                                    {PACKAGES.map((option) => (
                                        <option key={option}>{option}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <span>Industry</span>
                                <select name="industry" value={values.industry} onChange={handleChange} disabled={sending}>
                                    {INDUSTRIES.map((option) => (
                                        <option key={option}>{option}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="full">
                                <span>Message</span>
                                <textarea
                                    name="message"
                                    placeholder="Describe the payment flow, target market, and deployment preference."
                                    value={values.message}
                                    onChange={handleChange}
                                    aria-invalid={Boolean(errors.message)}
                                    disabled={sending}
                                    maxLength={4000}
                                />
                                {errors.message && <span className="field-error">{errors.message}</span>}
                            </label>

                            {/* Honeypot: hidden from users, catches naive bots. */}
                            <div className="hp-field" aria-hidden="true">
                                <label>
                                    <span>Website</span>
                                    <input
                                        type="text"
                                        name="website"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={values.website}
                                        onChange={handleChange}
                                    />
                                </label>
                            </div>

                            {challengeRequired && (
                                <div className="full challenge-box">
                                    <div ref={challengeRef} />
                                </div>
                            )}

                            <div className="full">
                                <button className="btn btn-primary" type="submit" disabled={sending}>
                                    {sending ? "Sending…" : "Request a discussion"}
                                </button>
                                {statusMessage && (
                                    <p className={`form-note form-note-${status}`} role="status" aria-live="polite">
                                        {statusMessage}
                                    </p>
                                )}
                                {RECAPTCHA_SITE_KEY && (
                                    <p className="recaptcha-notice">
                                        This site is protected by reCAPTCHA and the Google{" "}
                                        <a
                                            href="https://policies.google.com/privacy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Privacy Policy
                                        </a>{" "}
                                        and{" "}
                                        <a
                                            href="https://policies.google.com/terms"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Terms of Service
                                        </a>{" "}
                                        apply.
                                    </p>
                                )}
                            </div>
                        </form>
                    </div>
                    <div className="infra-band card">
                        <span className="kicker">Briefing points</span>
                        <h3>Helpful context</h3>
                        <ul className="check-list">
                            <li>Which solution matters most?</li>
                            <li>Which payment rail or wallet flow is in scope?</li>
                            <li>Do you need API, widget, or white-label?</li>
                            <li>Which markets and jurisdictions matter?</li>
                        </ul>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Contact;
